import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { supabase } from "@/lib/supabase";
import {
  useSEOMeta,
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generateFAQPageSchema,
} from "@/components/SEO";

const otherAreas = [
  { title: "Education", href: "/our-work/education", icon: "ri-book-open-line", color: "bg-amber-50 text-amber-700 border-amber-200" },
  { title: "Economic Security", href: "/our-work/economic-security", icon: "ri-seedling-line", color: "bg-teal-50 text-teal-700 border-teal-200" },
  { title: "Protection", href: "/our-work/protection", icon: "ri-shield-check-line", color: "bg-rose-50 text-rose-700 border-rose-200" },
];

export default function HealthPage() {
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useSEOMeta({
    title: "Health - BHODEV Cameroon | Emergency Response, Disease Control & Community Health",
    description:
      "BHODEV's health programs in Cameroon include emergency medical response, malaria and typhoid control, chronic disease prevention, and community health education across North West and South West regions.",
    keywords:
      "BHODEV health, Cameroon health NGO, emergency medical response Cameroon, malaria control Cameroon, community health education, chronic disease prevention, BHODEV health programs, North West Cameroon health, South West Cameroon health",
    canonicalUrl: "https://bhodevcameroon.org/our-work/health",
    ogImage:
      "https://readdy.ai/api/search-image?query=community%20health%20workers%20Cameroon%20providing%20medical%20care%20displaced%20families%20humanitarian%20setting%20green%20vegetation%20background%20warm%20natural%20light%20documentary%20style%20wide%20landscape&width=1200&height=630&seq=health-og&orientation=landscape",
    schemaData: {
      ...generateBreadcrumbSchema([
        { name: "Home", url: "https://bhodevcameroon.org/" },
        { name: "Our Work", url: "https://bhodevcameroon.org/our-work" },
        { name: "Health", url: "https://bhodevcameroon.org/our-work/health" },
      ]),
      ...generateWebPageSchema(
        "Health - BHODEV Cameroon",
        "BHODEV's health programs in Cameroon include emergency medical response, malaria and typhoid control, chronic disease prevention, and community health education.",
        "https://bhodevcameroon.org/our-work/health"
      ),
      ...generateFAQPageSchema([
        {
          question: "What health programs does BHODEV run in Cameroon?",
          answer:
            "BHODEV runs emergency medical response programs, malaria and typhoid control campaigns, chronic disease prevention initiatives, community health education, and childhood vaccination drives across Cameroon's North West and South West regions.",
        },
        {
          question: "How does BHODEV help with malaria control?",
          answer:
            "BHODEV conducts comprehensive need assessments and organizes regular free screening and treatment sessions for malaria and typhoid in communities across Cameroon's hinterland crisis-affected regions.",
        },
      ]),
    },
  });

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("projects")
      .select("*")
      .eq("sector", "Health")
      .order("created_at", { ascending: false });
    if (error) {
      console.error("Error fetching health projects:", error);
    } else {
      setProjects(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const fallbackProjects = [
    {
      title: "Emergency Health Response — NW/SW Cameroon",
      category: "Emergency Response",
      description: "Deploying emergency medical kits, training first aid responders, and conducting regular health surveillance in crisis-affected communities.",
      year: "2023",
      image: "https://readdy.ai/api/search-image?query=emergency%20medical%20response%20team%20in%20Cameroon%20deploying%20first%20aid%20kits%20to%20displaced%20communities%20humanitarian%20health%20workers%20in%20action%20documentary%20style&width=400&height=260&seq=hp1&orientation=landscape",
    },
    {
      title: "Malaria & Typhoid Free Communities",
      category: "Disease Control",
      description: "Comprehensive need assessments and free screening and treatment sessions for malaria and typhoid across communities in the North West and South West.",
      year: "2022",
      image: "https://readdy.ai/api/search-image?query=malaria%20prevention%20campaign%20in%20Cameroon%20health%20workers%20distributing%20mosquito%20nets%20and%20conducting%20screenings%20in%20rural%20community%20warm%20natural%20light&width=400&height=260&seq=hp2&orientation=landscape",
    },
    {
      title: "Chronic Disease Prevention Campaign",
      category: "Community Health",
      description: "Sensitizing communities on healthy eating habits, physical exercise, and regular health checks to reduce the burden of chronic diseases.",
      year: "2023",
      image: "https://readdy.ai/api/search-image?query=community%20health%20education%20session%20in%20Cameroon%20health%20workers%20teaching%20nutrition%20and%20wellness%20to%20local%20community%20members%20bright%20outdoor%20setting&width=400&height=260&seq=hp3&orientation=landscape",
    },
    {
      title: "Childhood Vaccination Drive",
      category: "Preventive Health",
      description: "Advocating for improved routine childhood vaccination and other actions aimed at reducing the spread of infectious diseases in crisis-affected regions.",
      year: "2022",
      image: "https://readdy.ai/api/search-image?query=childhood%20vaccination%20campaign%20in%20Cameroon%20nurse%20vaccinating%20young%20child%20with%20mother%20present%20clean%20medical%20setting%20warm%20caring%20atmosphere&width=400&height=260&seq=hp4&orientation=landscape",
    },
  ];

  const displayProjects = projects.length > 0 ? projects : fallbackProjects;

  return (
    <div className="min-h-screen bg-white font-inter">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[460px] md:h-[520px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://firebasestorage.googleapis.com/v0/b/bhodevcameroon.appspot.com/o/project-assets%2Fdistribution-of-water-hygiene-and-sanitation-kits-to-victims-of-flood-incident-in-limbe%2FIMG-20231010-WA0001.jpg?alt=media&token=7bc91039-c9b7-49e1-bfe8-22834353dc54"
            alt="BHODEV Health Work in Cameroon - Emergency Response and Disease Control"
            title="BHODEV Health Work in Cameroon - Emergency Response and Disease Control"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/65"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto w-full">
          <nav className="flex items-center justify-center gap-2 text-xs text-white/60 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white transition-colors duration-200">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/our-work" className="hover:text-white transition-colors duration-200">Our Work</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white/90">Health</span>
          </nav>
          <span className="inline-block bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 text-xs font-semibold px-4 py-1.5 rounded-full mb-5 tracking-wider uppercase">Core Sector</span>
          <h1 className="text-4xl md:text-5xl font-bold mb-5 leading-tight">Health</h1>
          <p className="text-lg md:text-xl text-white/85 max-w-2xl mx-auto leading-relaxed">
            Creating a future where everybody can enjoy a life of good health and well-being is our goal.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-14 px-4 md:px-8 lg:px-12 bg-emerald-50">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-10 items-start">
            <div className="w-full lg:w-3/5">
              <span className="text-xs font-semibold uppercase tracking-widest text-emerald-700 mb-3 block">Core Sectors — Health</span>
              <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-5">Our Approach to Health</h2>
              <p className="text-stone-700 text-sm md:text-base leading-relaxed mb-4">
                Good health is precious as when we are healthy, we can learn, plan, work and build a better tomorrow for the generations to come. <strong>BHODEV is actively driving the health agenda in Cameroon</strong> by working together with the Ministry of Public Health and other health-oriented organisations to fight against health challenges, as our staff are ready and determined in creating a healthier and better future together.
              </p>
            </div>
            <div className="w-full lg:w-2/5 grid grid-cols-2 gap-3">
              {[
                { icon: "ri-heart-pulse-line", label: "Emergency Response", value: "20+" },
                { icon: "ri-microscope-line", label: "Health Events/Year", value: "Acute" },
                { icon: "ri-community-line", label: "Communities Served", value: "50K+" },
                { icon: "ri-shield-cross-line", label: "SDG Aligned", value: "SDG 3" },
              ].map((stat) => (
                <div key={stat.label} className="bg-white rounded-xl p-4 border border-emerald-100 text-center">
                  <div className="w-8 h-8 flex items-center justify-center text-emerald-600 mx-auto mb-2">
                    <i className={`${stat.icon} text-xl`}></i>
                  </div>
                  <p className="text-lg font-bold text-stone-800">{stat.value}</p>
                  <p className="text-xs text-stone-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-14 px-4 md:px-8 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            <div>
              <h3 className="text-lg font-bold text-stone-800 mb-4 uppercase tracking-wide">Emergency Health Response</h3>
              <p className="text-stone-600 text-sm leading-relaxed mb-4">
                Every year, more than 20 acute public health events are reported in the crisis affected regions of Cameroon with a major focus on watching out for early warning signs, and helping the communities to get prepared. BHODEV is engaged in setting up systems to provide assistance to the communities when and where it's needed. We fully support the communities to respond effectively by conducting regular surveillance, training of first aid responders, deploying emergency medical kits and other key equipment.
              </p>
              <p className="text-stone-600 text-sm leading-relaxed">
                Africa faces high rate of infectious and chronic diseases especially in Cameroon where infectious diseases still account for most deaths in the country as deaths from chronic diseases are on the rise. <strong>BHODEV is playing its part in supporting communities to adapt to the evolving health landscape</strong> by advocating for improved routine childhood vaccination and other actions aimed at reducing the spread of infectious diseases. We find pleasure in promoting care for chronic conditions; while we are sensitizing the communities on the benefits of healthy eating habits, physical exercise and regular health checks.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-bold text-stone-800 mb-4 uppercase tracking-wide">Malaria &amp; Typhoid Control</h3>
              <p className="text-stone-600 text-sm leading-relaxed mb-4">
                Every year, BHODEV conducts a comprehensive need assessment of trends in typhoid &amp; malaria control across communities in the North West and South West as it is one of the major diseases that affects the people in the hinterland. The reports have revealed, for the first time, an intersection between climate change and the spread of malaria. That is climate change is one of many threats to the response to the treatment of malaria in the hinterland.
              </p>
              <p className="text-stone-600 text-sm leading-relaxed">
                Conflict and humanitarian crisis, resource constraints and biological challenges such as drug abuse also continue to hinder the progress as millions of people continue to miss out on the services they need to prevent, detect, and treat the disease. As such, <strong>BHODEV is organising regular sessions for free screening and treatment of the disease in communities among the crisis affected regions of Cameroon.</strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Projects */}
      <section className="py-14 px-4 md:px-8 lg:px-12 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-2">Organization Projects under Health</h2>
          <p className="text-stone-500 text-sm mb-10">Programmes and initiatives driving health outcomes in Cameroon</p>

          {loading && (
            <div className="flex items-center justify-center py-16">
              <i className="ri-loader-4-line animate-spin text-emerald-600 text-2xl"></i>
            </div>
          )}

          {!loading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {displayProjects.map((project) => (
                <Link
                  key={project.id || project.title}
                  to={project.id ? `/projects/${project.id}` : "#"}
                  className="bg-white rounded-2xl overflow-hidden border border-stone-100 hover:border-emerald-200 transition-all duration-300 group cursor-pointer block"
                >
                  <div className="w-full h-[160px]">
                    <img
                      src={project.image_url || project.image}
                      alt={project.title}
                      title={project.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <span className="inline-block bg-emerald-100 text-emerald-700 text-xs font-semibold px-2.5 py-1 rounded-full mb-3">
                      {project.status || project.category}
                    </span>
                    <h3 className="text-sm font-bold text-stone-800 mb-2 leading-snug">{project.title}</h3>
                    <p className="text-xs text-stone-500 leading-relaxed line-clamp-3">
                      {project.description || project.excerpt || ""}
                    </p>
                    <p className="text-xs text-stone-400 mt-3 font-medium">{project.start_date || project.year}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Other Core Areas */}
      <section className="py-12 px-4 md:px-8 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-lg font-bold text-stone-800 mb-6">Explore Other Core Areas</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {otherAreas.map((area) => (
              <Link
                key={area.title}
                to={area.href}
                className={`flex items-center gap-3 px-5 py-4 rounded-xl border ${area.color} hover:scale-105 transition-all duration-200 cursor-pointer`}
              >
                <div className="w-8 h-8 flex items-center justify-center shrink-0">
                  <i className={`${area.icon} text-xl`}></i>
                </div>
                <span className="font-semibold text-sm">{area.title}</span>
                <i className="ri-arrow-right-line ml-auto text-sm"></i>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}